import { ReactNode } from "react";
import { Box, styled } from "@mui/material";
import { Info } from "lucide-react";
import { tokens } from "@styles/tokens";

interface InfoCalloutProps {
  title: string;
  children: ReactNode;
}

/**
 * A quiet, brand-tinted callout used to set expectations before a form's
 * primary action ("Review before saving…"). Title on top, supporting copy
 * below, with a leading info glyph.
 */
export const InfoCallout = ({ title, children }: InfoCalloutProps) => (
  <Callout>
    <Glyph>
      <Info size={18} />
    </Glyph>
    <Text>
      <Title>{title}</Title>
      <Body>{children}</Body>
    </Text>
  </Callout>
);

const Callout = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: tokens.space(2.5),
  padding: tokens.space(3),
  borderRadius: tokens.radius.md,
  backgroundColor: theme.skuey.brandSubtleBg,
  border: `1px solid ${theme.skuey.brand[100]}`,
}));

const Glyph = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.skuey.brand[600],
}));

const Text = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

const Title = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.brand[700],
}));

const Body = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: theme.skuey.body,
}));
